import { useMemo } from "react"
import { ElementRenderer } from "../renderers/element-renderer"
import { RenderContext, useRenderContext } from "../core/render-context"
import { ComputeEvaluator } from "../runtime/compute-evaluator"
import type { CogoBlockDef } from "../core/types"

export interface CogoBlockProps {
  block: CogoBlockDef
}

export function CogoBlock({ block }: CogoBlockProps) {
  const computedDefs = block.variables?.computed
  const hasComputed = computedDefs && Object.keys(computedDefs).length > 0

  if (hasComputed) {
    return <BlockWithComputed block={block} computedDefs={computedDefs!} />
  }

  return <ElementRenderer element={block.root} />
}

/**
 * Wraps a block in a context that resolves computed properties and inline expressions.
 * This enables top-level blocks (not inside block-ref iteration) to use #{computed.xxx}
 * and inline expressions like #{product.rating != null}.
 */
function BlockWithComputed({ block, computedDefs }: {
  block: CogoBlockDef
  computedDefs: Record<string, string | number | boolean>
}) {
  const parentCtx = useRenderContext()

  const childCtx = useMemo(() => ({
    ...parentCtx,
    resolveSymbol: (expr: string) => {
      if (typeof expr !== "string") return parentCtx.resolveSymbol(expr)

      // Pure #{...} expression
      const pureMatch = /^#\{([^}]+)\}$/.exec(expr)
      if (pureMatch) {
        const inner = pureMatch[1].trim()

        // computed.xxx → evaluate computed expression
        if (inner.startsWith("computed.")) {
          const propName = inner.slice("computed.".length)
          const expression = computedDefs[propName]
          if (expression != null) {
            return evaluateBlockComputed(expression, parentCtx.resolveSymbol, computedDefs)
          }
        }

        // Inline expression (contains operators): #{product.rating != null}
        const isSimplePath = /^[a-zA-Z_][\w.]*$/.test(inner)
        if (!isSimplePath) {
          return evaluateBlockInline(inner, parentCtx.resolveSymbol, computedDefs)
        }

        // Simple path: delegate to parent
        return parentCtx.resolveSymbol(expr)
      }

      // Template with mixed #{...} patterns: "-#{computed.discountPercent}%"
      if (expr.includes("#{")) {
        return expr.replace(/#\{([^}]+)\}/g, (_, ref) => {
          const r = (ref as string).trim()

          if (r.startsWith("computed.")) {
            const propName = r.slice("computed.".length)
            const expression = computedDefs[propName]
            if (expression != null) {
              const val = evaluateBlockComputed(expression, parentCtx.resolveSymbol, computedDefs)
              return val != null ? String(val) : ""
            }
          }

          const isSimplePath = /^[a-zA-Z_][\w.]*$/.test(r)
          if (!isSimplePath) {
            const val = evaluateBlockInline(r, parentCtx.resolveSymbol, computedDefs)
            return val != null ? String(val) : ""
          }

          const val = parentCtx.resolveSymbol(`#{${ref}}`)
          return val != null ? String(val) : ""
        })
      }

      return parentCtx.resolveSymbol(expr)
    },
  }), [parentCtx, computedDefs])

  return (
    <RenderContext.Provider value={childCtx}>
      <ElementRenderer element={block.root} />
    </RenderContext.Provider>
  )
}

/**
 * Evaluate a computed expression from variables.cogo.json.
 * Uses a lightweight state adapter that delegates to resolveSymbol,
 * then evaluates via ComputeEvaluator (safe, no new Function()).
 *
 * When computedDefs is provided, computed.xxx references are resolved to their
 * evaluated values before the main expression — enabling chained computed dependencies.
 */
export function evaluateBlockComputed(
  expression: string | number | boolean,
  resolveSymbol: (expr: string) => unknown,
  computedDefs?: Record<string, string | number | boolean>,
): unknown {
  if (typeof expression !== "string") return expression

  try {
    // Pre-resolve computed.xxx dependencies
    let expr = expression
    if (computedDefs) {
      // Replace computed.xxx in the expression with their evaluated values
      expr = expr.replace(/computed\.([a-zA-Z_]\w*)/g, (_, propName) => {
        const computedExpr = computedDefs[propName]
        if (computedExpr != null) {
          const val = evaluateBlockComputed(computedExpr, resolveSymbol, computedDefs)
          if (val === null || val === undefined) return "null"
          if (typeof val === "string") return JSON.stringify(val)
          return String(val)
        }
        return "null"
      })
    }

    // Build a state adapter that delegates get/resolve to resolveSymbol
    const adapter = createResolveAdapter(resolveSymbol)
    const compute = new ComputeEvaluator(adapter)
    return compute.evaluate(expr)
  } catch {
    return undefined
  }
}

/**
 * Evaluate an inline expression like "product.rating != null" or "!computed.freeShipping".
 * Resolves computed.xxx references before evaluation.
 */
export function evaluateBlockInline(
  expression: string,
  resolveSymbol: (expr: string) => unknown,
  computedDefs: Record<string, string | number | boolean>,
): unknown {
  try {
    // Step 1: Resolve computed.xxx references
    let processed = expression.replace(
      /computed\.([a-zA-Z_]\w*)/g,
      (_, propName) => {
        const computedExpr = computedDefs[propName]
        if (computedExpr != null) {
          const val = evaluateBlockComputed(computedExpr, resolveSymbol, computedDefs)
          if (val === null || val === undefined) return "null"
          if (typeof val === "string") return JSON.stringify(val)
          return String(val)
        }
        return "null"
      },
    )

    // Step 2: Evaluate using ComputeEvaluator
    const adapter = createResolveAdapter(resolveSymbol)
    const compute = new ComputeEvaluator(adapter)
    return compute.evaluate(processed)
  } catch {
    return undefined
  }
}

/**
 * Create a StateAccessor adapter from a resolveSymbol function.
 * Maps resolveSymbol("#{path}") → get("path").
 */
function createResolveAdapter(resolveSymbol: (expr: string) => unknown) {
  return {
    get(path: string): unknown {
      return resolveSymbol(`#{${path}}`)
    },
    resolve(template: string): unknown {
      if (template.includes("#{")) {
        return resolveSymbol(template)
      }
      return template
    },
  }
}
